import {
  OperatorRank,
  OperatorRole,
  ProjectDepartment,
  ProjectHealth,
  ProjectPriority,
  TaskStatus,
  TeamMembership,
  TeamRole,
} from './types';
import { permLevel } from '../permissions/registry';

export const DB_PROJECTS = 'projects';
export const DB_WORKSTREAMS = 'workstreams';
export const DB_TASKS = 'tasks';
export const DB_COMMENTS = 'comments';
export const DB_NOTIFICATIONS = 'notifications';
export const DB_TEAMS = 'teams';
export const DB_TEAM_MEMBERS = 'teamMembers';
export const DB_PROJECT_TEMPLATES = 'projectTemplates';
export const DB_TEMPLATE_WORKSTREAMS = 'templateWorkstreams';
export const DB_TEMPLATE_TASKS = 'templateTasks';
export const DB_PROJECT_DEPARTMENTS = 'projectDepartments';
// Tiny meta record alongside the departments collection. Tracks whether
// the workspace has been seeded with the four built-in departments
// (audit/implementation/strategic/brand). Once seeded=true, the seeder
// never re-runs — that's what lets users delete all four defaults
// without them coming back.
export const DB_PROJECT_DEPARTMENTS_META = 'projectDepartmentsMeta';

export const MAX_COMMENT_DEPTH = 6;
export const NOTIFICATION_PREVIEW_LEN = 120;
export const NOTIFICATIONS_DISPLAYED = 12;
export const DEFAULT_TEAM_SEAT_LIMIT = 10;

// Workstream color palette. First entry is the default brand red; rest are
// neutral-saturation hues that read well against white surfaces.
export const WORKSTREAM_COLORS: { value: string; label: string }[] = [
  { value: '#C2433D', label: 'Red' },
  { value: '#D97706', label: 'Orange' },
  { value: '#CA8A04', label: 'Amber' },
  { value: '#15803D', label: 'Green' },
  { value: '#0F766E', label: 'Teal' },
  { value: '#1D4ED8', label: 'Blue' },
  { value: '#4338CA', label: 'Indigo' },
  { value: '#7E22CE', label: 'Purple' },
  { value: '#BE185D', label: 'Pink' },
  { value: '#4B5563', label: 'Gray' },
];

export const DEFAULT_WORKSTREAM_COLOR = '#9CA3AF'; // muted gray when none set

export const TEAM_ROLE_LABELS: Record<TeamRole, string> = {
  super_admin: 'Super admin',
  admin: 'Admin',
  counselor: 'Counselor',
  readonly: 'Read-only',
};

// Strictest first — used by role pickers and "who outranks whom" comparisons.
export const TEAM_ROLE_ORDER: TeamRole[] = ['super_admin', 'admin', 'counselor', 'readonly'];

// Numeric rank for "X outranks Y" checks. Higher = more authority. Don't
// hardcode 0/1/2/3 in components; compare via TEAM_ROLE_RANK[role].
export const TEAM_ROLE_RANK: Record<TeamRole, number> = {
  super_admin: 3,
  admin: 2,
  counselor: 1,
  readonly: 0,
};

// ---------- Team capability layer ----------
//
// Roles assign a default capability bundle; per-membership extra/revoked
// capabilities let you customize without inventing new roles. This is the
// "modular and specific custom permissions system" hook — UI can render
// toggles for any of these, and rules/code can call hasCapability().
//
// Capability ids are stable strings (NOT enum positions) so additions /
// removals don't shift existing data.
export const TEAM_CAPABILITIES = {
  // Team data & settings
  MANAGE_TEAM_SETTINGS: 'team:manage_settings',         // rename, seat limit, delete
  MANAGE_MEMBERS:       'team:manage_members',          // add/remove members
  PROMOTE_MEMBERS:      'team:promote_members',         // change a member's role
  // Project-management surface (mirrors PM suite verbs)
  VIEW_PROJECTS:        'projects:view',
  CREATE_PROJECTS:      'projects:create',
  EDIT_PROJECTS:        'projects:edit',
  DELETE_PROJECTS:      'projects:delete',
  COMMENT_ON_TASKS:     'tasks:comment',
  ASSIGN_TASKS:         'tasks:assign',
  // Reserved for future granular surfaces; add new ids here as workflows grow.
} as const;

export type TeamCapability = (typeof TEAM_CAPABILITIES)[keyof typeof TEAM_CAPABILITIES];

// Default capability bundle per role. Customize at the membership level via
// extra/revoked capabilities rather than editing this map.
export const ROLE_DEFAULT_CAPABILITIES: Record<TeamRole, TeamCapability[]> = {
  super_admin: [
    TEAM_CAPABILITIES.MANAGE_TEAM_SETTINGS,
    TEAM_CAPABILITIES.MANAGE_MEMBERS,
    TEAM_CAPABILITIES.PROMOTE_MEMBERS,
    TEAM_CAPABILITIES.VIEW_PROJECTS,
    TEAM_CAPABILITIES.CREATE_PROJECTS,
    TEAM_CAPABILITIES.EDIT_PROJECTS,
    TEAM_CAPABILITIES.DELETE_PROJECTS,
    TEAM_CAPABILITIES.COMMENT_ON_TASKS,
    TEAM_CAPABILITIES.ASSIGN_TASKS,
  ],
  admin: [
    TEAM_CAPABILITIES.MANAGE_MEMBERS,
    TEAM_CAPABILITIES.VIEW_PROJECTS,
    TEAM_CAPABILITIES.CREATE_PROJECTS,
    TEAM_CAPABILITIES.EDIT_PROJECTS,
    TEAM_CAPABILITIES.COMMENT_ON_TASKS,
    TEAM_CAPABILITIES.ASSIGN_TASKS,
  ],
  counselor: [
    TEAM_CAPABILITIES.VIEW_PROJECTS,
    TEAM_CAPABILITIES.COMMENT_ON_TASKS,
    TEAM_CAPABILITIES.ASSIGN_TASKS,
  ],
  readonly: [
    TEAM_CAPABILITIES.VIEW_PROJECTS,
  ],
};

// Resolve a membership's effective capability set: role defaults plus any
// extras, minus any revoked. Useful for client-side UI gating and for any
// future Cloud Function that needs to enforce capabilities server-side.
//
// Note: Firestore rules can't run this resolver against the role's default
// list directly (rules can't import constants); for rule-level enforcement of
// fine-grained capabilities you'd need to denormalize the effective set onto
// the TeamMembership doc. We're not doing that yet — current rules enforce at
// the role tier, and capability checks happen in code.
export function effectiveCapabilities(membership: Pick<TeamMembership, 'role' | 'extraCapabilities' | 'revokedCapabilities'>): Set<string> {
  const set = new Set<string>(ROLE_DEFAULT_CAPABILITIES[membership.role] ?? []);
  for (const c of membership.extraCapabilities || []) set.add(c);
  for (const c of membership.revokedCapabilities || []) set.delete(c);
  return set;
}

export function hasCapability(
  membership: Pick<TeamMembership, 'role' | 'extraCapabilities' | 'revokedCapabilities'>,
  capability: string,
): boolean {
  return effectiveCapabilities(membership).has(capability);
}

// ---------- Operator rank (access tier) ----------
//
// Three tiers, strictest first. Rank gates promotions/demotions and any
// sensitive write that should be restricted to leadership. Rank is orthogonal
// to OperatorRole (the functional tag below).
export const OPERATOR_RANK_LABELS: Record<OperatorRank, string> = {
  super_admin: 'Operator super admin',
  admin: 'Operator admin',
  standard: 'Operator',
};

export const OPERATOR_RANK_ORDER: OperatorRank[] = ['super_admin', 'admin', 'standard'];

export const OPERATOR_RANK_VALUE: Record<OperatorRank, number> = {
  super_admin: 2,
  admin: 1,
  standard: 0,
};

// Back-compat resolver: derive the new OperatorRank from a user's stored
// restricted map. Prefers the unified permissions grant
// (restricted.permissions.operator.level); then a stored operatorRank; then
// the legacy operatorRole hierarchy values ('super_admin', 'operator_lead');
// otherwise defaults to 'standard'. Returns null if the user isn't an
// operator at all (no operator grant and restricted.role !== 'admin').
//
// NOTE: the operator gate + shell selection in App.tsx still key on
// restricted.role === 'admin', and the backfill only writes
// permissions.operator for users who already have role === 'admin', so the
// unified grant and the legacy gate stay in sync.
export function resolveOperatorRank(restricted: {
  role?: string;
  operatorRank?: string;     // loose: accepts raw Firestore string before narrowing
  operatorRole?: string;
  permissions?: Record<string, unknown>;
} | undefined | null): OperatorRank | null {
  if (!restricted) return null;
  // Unified permissions map takes precedence.
  const p = permLevel(restricted, 'operator');
  if (p === 'super_admin' || p === 'admin' || p === 'standard') {
    return p as OperatorRank;
  }
  if (restricted.role !== 'admin') return null;
  switch (restricted.operatorRank) {
    case 'super_admin':
    case 'admin':
    case 'standard':
      return restricted.operatorRank as OperatorRank;
  }
  // Legacy mapping for users who haven't been backfilled yet.
  if (restricted.operatorRole === 'super_admin') return 'super_admin';
  if (restricted.operatorRole === 'operator_lead') return 'admin';
  return 'standard';
}

// Operator functional roles (Rollover Rep staff). The 'super_admin' and
// 'operator_lead' values are legacy — they previously doubled as hierarchy
// levels and remain accepted on existing docs; resolveOperatorRank() maps
// them to the new OperatorRank.
export const OPERATOR_ROLE_LABELS: Record<OperatorRole, string> = {
  super_admin: 'Super admin',
  operator_lead: 'Operator lead',
  engineer: 'Engineer',
  support: 'Support',
  finance: 'Finance',
  agent: 'Agent',
};

export const OPERATOR_ROLE_ORDER: OperatorRole[] = [
  'super_admin',
  'operator_lead',
  'engineer',
  'support',
  'finance',
  'agent',
];

// Filter tabs on the Internal Team page. `roles` = empty array means "All".
export interface OperatorTabFilter {
  key: string;
  label: string;
  roles: OperatorRole[];
}

export const OPERATOR_TAB_FILTERS: OperatorTabFilter[] = [
  { key: 'all', label: 'All', roles: [] },
  { key: 'engineering', label: 'Engineering', roles: ['engineer'] },
  { key: 'support', label: 'Support', roles: ['support'] },
  { key: 'operations', label: 'Operations', roles: ['operator_lead'] },
  { key: 'finance', label: 'Finance', roles: ['finance'] },
  { key: 'agents', label: 'Agents', roles: ['agent'] },
];

// Deterministic avatar color from a string.
const AVATAR_PALETTE = [
  '#C2433D', '#2A6FB0', '#1E8E5A', '#7A4DB0', '#B86A1F',
  '#0F7A8A', '#7A1F4A', '#3C5A99', '#357A3C', '#9C5A0F',
];

export function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) | 0;
  }
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}

export function initialsFor(email: string | undefined | null): string {
  if (!email) return '?';
  const local = email.split('@')[0] || '';
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return (local.slice(0, 2) || '?').toUpperCase();
}

export const STATUS_LABELS: Record<TaskStatus, string> = {
  todo: 'To do',
  progress: 'In progress',
  done: 'Done',
  blocked: 'Blocked',
};

export const STATUS_ORDER: TaskStatus[] = ['todo', 'progress', 'done', 'blocked'];

// ---------- Project priority (roadmap) ----------
// Stored key vs. friendly label kept separate so we can rename the friendly
// side (e.g. "ASAP") without an RTDB migration.
export const PROJECT_PRIORITY_ORDER: ProjectPriority[] = ['asap', 'high', 'medium', 'low'];

export const PROJECT_PRIORITY_LABELS: Record<ProjectPriority, string> = {
  asap: 'ASAP',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

// Lane / chip colors. Roughly the same red→gray ramp used for due-date tones,
// chosen so adjacent priorities are easily distinguishable.
export const PROJECT_PRIORITY_COLORS: Record<ProjectPriority, string> = {
  asap: '#C2433D',
  high: '#D97706',
  medium: '#1D4ED8',
  low: '#6B7280',
};

// Numeric weight used for sorting; the "Unprioritized" bucket sorts after all
// real priorities so it shows at the bottom of the roadmap.
export const PROJECT_PRIORITY_RANK: Record<ProjectPriority, number> = {
  asap: 0,
  high: 1,
  medium: 2,
  low: 3,
};
export const PROJECT_PRIORITY_UNSET_RANK = 4;

// "Soft" backgrounds for priority pills / lane tints — translucent versions
// of the priority swatches that read well on a paper surface.
export const PROJECT_PRIORITY_SOFT: Record<ProjectPriority, string> = {
  asap:   '#FEF1F1',
  high:   '#FBF3E3',
  medium: '#EAF1F7',
  low:    '#F0F1F1',
};

// "Done" isn't a priority value (it doesn't belong in PROJECT_PRIORITY_ORDER —
// done projects leave the Grid lanes entirely). It's a separate completed
// state shown in the left-side Done rail and as an option in the priority
// picker, so it gets its own green swatch / label here.
export const PROJECT_DONE_LABEL = 'Done';
export const PROJECT_DONE_COLOR = '#15803D';
export const PROJECT_DONE_SOFT = '#E3F3EB';

// ---------- Department ----------

export interface DepartmentDef {
  id: ProjectDepartment;
  label: string;
  swatch: string; // 8px dot color in lane headers / chips
  color: string;  // saturated edge for borders and accents
}

/** Built-in departments — always present, can't be deleted. Customs live
 *  in RTDB and are appended to this list at runtime by useProjectDepartments. */
export const DEFAULT_DEPARTMENTS: DepartmentDef[] = [
  { id: 'audit',          label: 'Audit',              swatch: '#3A6066', color: '#20454A' },
  { id: 'implementation', label: 'Implementation',     swatch: '#5A8CB3', color: '#43799F' },
  { id: 'strategic',      label: 'Strategic Accounts', swatch: '#D9933A', color: '#C58A2E' },
  { id: 'brand',          label: 'Brand & Marketing',  swatch: '#9272D8', color: '#7A4FCC' },
];
/** Back-compat alias. Use DEFAULT_DEPARTMENTS in new code. */
export const DEPARTMENTS = DEFAULT_DEPARTMENTS;

/** IDs of the built-ins. UI uses this to gate the delete affordance —
 *  built-ins are never deletable, only customs are. */
export const DEFAULT_DEPARTMENT_IDS = new Set(DEFAULT_DEPARTMENTS.map((d) => d.id));

/** Lookup against the built-ins. Components that need the merged
 *  (built-in + custom) list should call useProjectDepartments() and use
 *  its helper instead. */
export function getDepartment(id: ProjectDepartment | undefined): DepartmentDef | undefined {
  if (!id) return undefined;
  return DEFAULT_DEPARTMENTS.find((d) => d.id === id);
}

/** Soft palette for custom department swatches — picked when the user adds
 *  a new department without specifying a color. We just cycle through a
 *  small set so multiple new ones look distinct. */
export const DEPARTMENT_CUSTOM_PALETTE: Array<{ swatch: string; color: string }> = [
  { swatch: '#5A8CB3', color: '#43799F' }, // blue
  { swatch: '#3A6066', color: '#20454A' }, // teal
  { swatch: '#9272D8', color: '#7A4FCC' }, // purple
  { swatch: '#D9933A', color: '#C58A2E' }, // amber
  { swatch: '#2F8F5E', color: '#1F6843' }, // green
  { swatch: '#E55C57', color: '#B12728' }, // red
];

// ---------- Health ----------

export const PROJECT_HEALTH_ORDER: ProjectHealth[] = ['on-track', 'at-risk', 'off-track'];

export const PROJECT_HEALTH_LABELS: Record<ProjectHealth, string> = {
  'on-track':  'On track',
  'at-risk':   'At risk',
  'off-track': 'Off track',
};

export function nextStatus(s: TaskStatus): TaskStatus {
  const i = STATUS_ORDER.indexOf(s);
  return STATUS_ORDER[(i + 1) % STATUS_ORDER.length];
}

export function displayName(email: string | undefined | null): string {
  if (!email) return 'unassigned';
  const local = email.split('@')[0];
  if (!local) return email;
  return local.charAt(0).toUpperCase() + local.slice(1);
}
